import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import FormControl from '@zenbusiness/zui/FormControl';
import FormHelperText from '@zenbusiness/zui/FormHelperText';
import MenuItem from '@zenbusiness/zui/MenuItem';
import Select from '@zenbusiness/zui/Select';

import FieldHeading from './FieldHeading';
import { getFieldOptions } from './FieldOptionRegistry';

/**
 * Dropdown single select field
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {object} props.value - Current selected value {value, label}
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 */
function DropdownSingleSelectField({ def, value, onChange, disabled = false, error }) {
  const options = getFieldOptions(def.options) || [];

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    const selectedOption = options.find((opt) => opt.value === selectedValue);

    if (selectedOption) {
      onChange({
        value: selectedOption.value,
        label: selectedOption.label
      });
    }
  };

  const fieldId = `dropdown-single-select-${def.id}`;
  const hasError = Boolean(error);
  const selectedValue = value?.value || '';

  return (
    <FormControl
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
      variant="outlined"
    >
      <FieldHeading
        description={def.description}
        label={def.label}
        labelId={`${fieldId}-label`}
        required={def.required}
      />

      <Box mt={2}>
        <Select
          disabled={disabled}
          displayEmpty={Boolean(def.placeholder)}
          id={fieldId}
          label={def.label}
          labelId={`${fieldId}-label`}
          value={selectedValue}
          onChange={handleChange}
        >
          {def.placeholder && (
            <MenuItem disabled value="">
              {def.placeholder}
            </MenuItem>
          )}
          {options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {(error || def.helpText) && <FormHelperText>{error || def.helpText}</FormHelperText>}
    </FormControl>
  );
}

DropdownSingleSelectField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['dropdown_single_select']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
  }).isRequired,
  value: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default DropdownSingleSelectField;
